// Settings → Prioritization (admin only, 2026-10-01). Every intent with its
// Queue priority level (0-3, lower sorts first; oldest first within a level),
// stored in intent_priorities (migration 0093). Changing a level asks for
// confirmation first — the same typed-confirmation dialog as the Flows
// catalog's Automate toggle — because it reorders every agent's Queue. Each
// change is recorded; "View history" lists them, newest first. Below the
// intent levels, the quick-reply rules (QuickReplyRulesSection.jsx, 2026-10-29).
import { useEffect, useMemo, useState, useCallback } from 'react'
import {
  Box, Stack, Typography, Button, TextField, Paper, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, Link,
  Table, TableHead, TableBody, TableRow, TableCell,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material'
import { fetchAdminIntentPriorities, updateIntentPriority, fetchIntentPriorityHistory, INTENT_PRIORITIES_CHANGED_EVENT } from '../../api.js'
import { filterAndSortPriorities, PRIORITY_LEVELS, priorityChangeMessage } from '../../prioritization.js'
import QuickReplyRulesSection from './QuickReplyRulesSection.jsx'

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/New_York', month: 'short', day: 'numeric',
    year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  }) + ' ET'
}

// The Flows toggle's pattern: the confirm button stays disabled until the
// admin types the intent's value exactly.
function PriorityChangeConfirmDialog({ change, saving, error, onConfirm, onCancel }) {
  const [typed, setTyped] = useState('')
  useEffect(() => { if (!change) setTyped('') }, [change])
  return (
    <Dialog open={Boolean(change)} onClose={() => !saving && onCancel()} maxWidth="xs" fullWidth>
      <DialogTitle>Change priority level</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Typography>{change ? priorityChangeMessage(change) : ''}</Typography>
        <TextField
          size="small"
          fullWidth
          label="Type the intent to confirm"
          placeholder={change?.intent}
          helperText={`Type "${change?.intent ?? ''}" exactly to enable the confirm button.`}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          disabled={saving}
          sx={{ mt: 2 }}
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={saving || typed !== change?.intent}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {saving ? 'Saving…' : 'Change level'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function PriorityHistoryDialog({ open, onClose }) {
  const [history, setHistory] = useState(null)
  const [error, setError] = useState(null)
  useEffect(() => {
    if (!open) return
    setHistory(null)
    setError(null)
    fetchIntentPriorityHistory().then(setHistory).catch((e) => setError(e.message))
  }, [open])
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Priority level history</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        {history === null && !error ? (
          <CircularProgress size={18} />
        ) : history?.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No changes recorded yet.</Typography>
        ) : history ? (
          <Table size="small" data-testid="priority-history">
            <TableHead>
              <TableRow>
                <TableCell>Intent</TableCell>
                <TableCell>Change</TableCell>
                <TableCell>Date / time</TableCell>
                <TableCell>User</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((h) => (
                <TableRow key={h.id}>
                  <TableCell>{h.label} <Typography component="span" variant="caption" color="text.secondary">({h.intent})</Typography></TableCell>
                  <TableCell>Level {h.oldTier ?? '—'} → Level {h.newTier}</TableCell>
                  <TableCell>{formatDate(h.changedAt)}</TableCell>
                  <TableCell>{h.changedBy ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  )
}

export default function PrioritizationPage() {
  const [intents, setIntents] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [levelFilter, setLevelFilter] = useState([])
  const [search, setSearch] = useState('')
  const [pending, setPending] = useState(null) // { intent, label, fromTier, toTier }
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [historyOpen, setHistoryOpen] = useState(false)

  const load = useCallback(() => {
    fetchAdminIntentPriorities().then((d) => setIntents(d.intents ?? [])).catch((e) => setLoadError(e.message))
  }, [])
  useEffect(() => { load() }, [load])

  const rows = useMemo(() => filterAndSortPriorities(intents ?? [], { levels: levelFilter, search }), [intents, levelFilter, search])

  const confirmChange = async () => {
    if (!pending) return
    setSaving(true)
    setSaveError(null)
    try {
      const res = await updateIntentPriority(pending.intent, pending.toTier)
      setIntents((prev) => prev.map((i) => (i.intent === pending.intent ? { ...i, tier: res.tier, updatedBy: res.updatedBy, updatedAt: res.updatedAt } : i)))
      // This tab's own Queue re-sorts at once; other tabs pick it up on their
      // next refresh.
      window.dispatchEvent(new Event(INTENT_PRIORITIES_CHANGED_EVENT))
      setPending(null)
    } catch (e) {
      setSaveError(e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="baseline" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="h6">Prioritization</Typography>
        <Link component="button" variant="body2" onClick={() => setHistoryOpen(true)}>View history</Link>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The Queue's Oldest First order: Level 0 first, then 1, 2 and 3, oldest first within each level. Newest First and Closed are not affected.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel shrink>Level</InputLabel>
          <Select
            multiple
            displayEmpty
            label="Level"
            value={levelFilter}
            onChange={(e) => setLevelFilter(typeof e.target.value === 'string' ? e.target.value.split(',').map(Number) : e.target.value)}
            renderValue={(sel) => (sel.length === 0 ? 'All levels' : sel.slice().sort().map((l) => `Level ${l}`).join(', '))}
            inputProps={{ 'aria-label': 'Filter by level' }}
          >
            {PRIORITY_LEVELS.map((l) => (
              <MenuItem key={l} value={l}>
                <Checkbox size="small" checked={levelFilter.includes(l)} />
                <ListItemText primary={`Level ${l}`} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Search intents"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
        />
      </Stack>

      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
      {intents === null && !loadError ? (
        <CircularProgress size={20} />
      ) : (
        <Paper variant="outlined">
          <Table size="small" data-testid="prioritization-table">
            <TableHead>
              <TableRow>
                <TableCell>Intent</TableCell>
                <TableCell>Value</TableCell>
                <TableCell sx={{ width: 140 }}>Level</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((i) => (
                <TableRow key={i.intent} data-testid={`priority-row-${i.intent}`}>
                  <TableCell>{i.label}</TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{i.intent}</Typography></TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={i.tier}
                      onChange={(e) => {
                        const toTier = Number(e.target.value)
                        if (toTier !== i.tier) setPending({ intent: i.intent, label: i.label, fromTier: i.tier, toTier })
                      }}
                      inputProps={{ 'aria-label': `Level for ${i.label}` }}
                    >
                      {PRIORITY_LEVELS.map((l) => <MenuItem key={l} value={l}>{`Level ${l}`}</MenuItem>)}
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={3}><Typography variant="body2" color="text.secondary">No intents match.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <PriorityChangeConfirmDialog
        change={pending}
        saving={saving}
        error={saveError}
        onConfirm={confirmChange}
        onCancel={() => { setPending(null); setSaveError(null) }}
      />
      <PriorityHistoryDialog open={historyOpen} onClose={() => setHistoryOpen(false)} />

      {/* Below the intent levels (2026-10-29): rules that raise a contact's
          level regardless of intent. */}
      <QuickReplyRulesSection />
    </Box>
  )
}
